import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Dices, Briefcase, TrendingUp, Brain, Award, Target } from "lucide-react";

const EducationAdult = () => {
  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Random Number Generator for Adult Learning & Professional Development
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Free tools for continuing education, professional training, certification prep, and workforce development. 
            Perfect for adult learners, training coordinators, and professional development programs.
          </p>
        </header>

        {/* Key Benefits */}
        <section className="mb-16">
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-6">
              <Briefcase className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Professional Training</h3>
              <p className="text-muted-foreground">
                Enhance workplace training programs with interactive exercises, randomized scenarios, and fair team assignments for skills development.
              </p>
            </Card>
            <Card className="p-6">
              <Brain className="w-12 h-12 text-accent mb-4" />
              <h3 className="text-xl font-semibold mb-2">Certification Prep</h3>
              <p className="text-muted-foreground">
                Create randomized practice tests, study drills, and exam simulations for professional certifications and licensing exams.
              </p>
            </Card>
            <Card className="p-6">
              <TrendingUp className="w-12 h-12 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Career Advancement</h3>
              <p className="text-muted-foreground">
                Support career changers and upskilling programs with tools for data analysis training, technical skills practice, and project management.
              </p>
            </Card>
          </div>
        </section>

        {/* Use Cases by Sector */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Applications for Adult Education</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <Briefcase className="w-10 h-10 text-primary mb-3" />
              <h3 className="text-2xl font-semibold mb-3">Corporate Training & HR</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Team Building:</strong> Random group assignments for workshops, icebreaker activities, and cross-departmental collaboration</li>
                <li>• <strong>Training Exercises:</strong> Randomized case studies, role-play scenarios, customer service simulations</li>
                <li>• <strong>Performance Reviews:</strong> Random sampling for 360-degree feedback, peer evaluation selection, quality audits</li>
                <li>• <strong>Skills Assessment:</strong> Generate random problem sets for technical skills testing, typing speed drills, data entry accuracy</li>
              </ul>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <Award className="w-10 h-10 text-accent mb-3" />
              <h3 className="text-2xl font-semibold mb-3">Professional Certifications</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Exam Preparation:</strong> Randomized practice questions for PMP, CPA, CFA, CISSP, NCLEX, and other professional exams</li>
                <li>• <strong>Continuing Education:</strong> Random topic selection for CPE credits, CEU courses, professional development hours</li>
                <li>• <strong>License Renewal:</strong> Study drills for real estate, insurance, nursing, teaching, and trade license exams</li>
                <li>• <strong>Bootcamps:</strong> Coding challenges, data science exercises, cybersecurity simulations with random parameters</li>
              </ul>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <Brain className="w-10 h-10 text-primary mb-3" />
              <h3 className="text-2xl font-semibold mb-3">Community College & Adult Ed</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>GED/HiSET Prep:</strong> Math practice with random problem generation, testing simulation, study randomization</li>
                <li>• <strong>ESL Programs:</strong> Vocabulary drills, pronunciation practice order, conversational partner randomization</li>
                <li>• <strong>Vocational Training:</strong> Technical skills practice for welding, HVAC, electrical, plumbing certification programs</li>
                <li>• <strong>Workforce Development:</strong> Job readiness programs, soft skills workshops, computer literacy training</li>
              </ul>
            </div>

            <div className="bg-card/50 backdrop-blur-sm rounded-lg p-6 border border-border/50">
              <TrendingUp className="w-10 h-10 text-accent mb-3" />
              <h3 className="text-2xl font-semibold mb-3">Career Transition & Upskilling</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• <strong>Data Analytics:</strong> Generate random datasets for Excel training, SQL practice, Tableau/Power BI exercises</li>
                <li>• <strong>Project Management:</strong> Monte Carlo simulations for risk analysis, random task prioritization, resource allocation</li>
                <li>• <strong>Digital Marketing:</strong> A/B testing scenarios, audience segmentation practice, conversion rate optimization exercises</li>
                <li>• <strong>Software Development:</strong> Test case generation, random input testing, algorithm practice problems</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Training Coordinator Tools */}
        <section className="mb-16 bg-card/30 backdrop-blur-sm rounded-lg p-8 border border-border/50">
          <h2 className="text-3xl font-bold text-center mb-6">For Training Coordinators & Instructors</h2>
          <div className="max-w-4xl mx-auto space-y-6">
            <div>
              <h4 className="text-xl font-semibold mb-3">Classroom Management</h4>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li>• Randomly assign adult learners to breakout groups or project teams</li>
                <li>• Fair selection for presentation order, discussion starters, or demonstration volunteers</li>
                <li>• Generate random partner pairings for peer learning and accountability partners</li>
                <li>• Randomize seat assignments for testing to ensure integrity</li>
              </ul>
            </div>
            <div>
              <h4 className="text-xl font-semibold mb-3">Engagement Strategies</h4>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li>• <strong>Gamification:</strong> Random prize drawings for participation, attendance incentives, course completion rewards</li>
                <li>• <strong>Interactive Polling:</strong> Randomly select participants to share opinions or answer reflection questions</li>
                <li>• <strong>Icebreakers:</strong> Generate random numbers for "two truths and a lie", speed networking rotations, team challenges</li>
                <li>• <strong>Energy Boosters:</strong> Random selection for energizer activities, stretch break leaders, recap volunteers</li>
              </ul>
            </div>
            <div>
              <h4 className="text-xl font-semibold mb-3">Assessment & Evaluation</h4>
              <ul className="space-y-2 text-muted-foreground ml-4">
                <li>• Create randomized quizzes and tests with different question orders for each learner</li>
                <li>• Generate unique practice problem sets to prevent sharing answers</li>
                <li>• Random sampling for course evaluations, focus groups, feedback sessions</li>
                <li>• Randomize practical exam scenarios for skills demonstrations</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Tools Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-6">Professional Development Tools</h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/">
              <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer h-full">
                <Target className="w-12 h-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Number Generator</h3>
                <p className="text-muted-foreground">Generate random data for training exercises, simulations, and practice scenarios</p>
              </Card>
            </Link>
            <Link to="/games">
              <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer h-full">
                <Dices className="w-12 h-12 text-accent mb-4" />
                <h3 className="text-xl font-semibold mb-2">Decision Tools</h3>
                <p className="text-muted-foreground">Use dice and coin flips for fair selection and probability-based training activities</p>
              </Card>
            </Link>
            <Link to="/lists">
              <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer h-full">
                <Briefcase className="w-12 h-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">Team Organizer</h3>
                <p className="text-muted-foreground">Shuffle and organize participant lists for group activities and assignments</p>
              </Card>
            </Link>
          </div>
        </section>

        {/* Industry Applications */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Industry-Specific Applications</h2>
          <div className="max-w-4xl mx-auto bg-card/50 backdrop-blur-sm rounded-lg p-8 border border-border/50">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-lg mb-3">Healthcare & Medical</h4>
                <ul className="space-y-1 text-muted-foreground text-sm">
                  <li>• NCLEX-RN, USMLE, MCAT practice question randomization</li>
                  <li>• Patient case study selection for training</li>
                  <li>• Clinical rotation assignments</li>
                  <li>• Medical simulation scenario generation</li>
                  <li>• CME course activity randomization</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-3">Technology & IT</h4>
                <ul className="space-y-1 text-muted-foreground text-sm">
                  <li>• CompTIA, AWS, Azure certification practice</li>
                  <li>• Coding bootcamp challenge generation</li>
                  <li>• Cybersecurity penetration test scenarios</li>
                  <li>• Database query practice with random data</li>
                  <li>• Network troubleshooting simulations</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-3">Finance & Business</h4>
                <ul className="space-y-1 text-muted-foreground text-sm">
                  <li>• CPA, CFA, CFP exam preparation</li>
                  <li>• Financial modeling with random variables</li>
                  <li>• Risk assessment Monte Carlo simulations</li>
                  <li>• Accounting practice problem generation</li>
                  <li>• Business analytics case studies</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-3">Trades & Technical</h4>
                <ul className="space-y-1 text-muted-foreground text-sm">
                  <li>• Electrical, plumbing, HVAC license prep</li>
                  <li>• Safety training scenario randomization</li>
                  <li>• Measurement and calculation drills</li>
                  <li>• Equipment calibration practice</li>
                  <li>• Troubleshooting sequence generation</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-12">
          <h2 className="text-3xl font-bold mb-4">Free Tools for Lifelong Learners</h2>
          <p className="text-xl text-muted-foreground mb-6 max-w-2xl mx-auto">
            No registration required. Professional-grade tools completely free for adult learners, trainers, and career professionals.
          </p>
          <Link to="/">
            <Button size="lg" className="text-lg px-8">
              Start Using Professional Tools
            </Button>
          </Link>
        </section>
      </div>
    </Layout>
  );
};

export default EducationAdult;
